/**
 * Data Validation Utilities
 * Provides robust validation and fallback handling for API responses
 */

/**
 * Validates and sanitizes analytics data from API response
 * @param {Object} data - Raw API response data
 * @returns {Object} Validated and sanitized analytics data
 */
export const validateAnalyticsData = (data) => {
  if (!data || typeof data !== 'object') {
    return getAnalyticsDefaults();
  }

  // Validate numeric fields
  const numericFields = [
    'totalRevenue',
    'totalCommission', 
    'totalOrders',
    'processedOrders',
    'monthlyGrowth',
    'commissionGrowth',
    'averageOrderValue',
    'averageCommissionRate',
    'totalShippingFees',
    'totalGrossRevenue',
    'totalNetSales',
    'totalPayout',
    'totalRefunds',
    'totalReturnFees',
    'totalShippingFees',
    'totalDiscounts',
    'topBrand',
    'topMarketplace'
  ];

  const validated = {};

  numericFields.forEach(field => {
    const value = data[field];
    if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
      validated[field] = value;
    } else if (typeof value === 'string' && !isNaN(parseFloat(value))) {
      validated[field] = parseFloat(value);
    } else {
      validated[field] = 0;
    }
  });

  // Validate string fields
  validated.topBrand = typeof data.topBrand === 'string' ? data.topBrand : '';
  validated.topMarketplace = typeof data.topMarketplace === 'string' ? data.topMarketplace : '';

  // Validate calculated fields
  if (validated.totalOrders === 0 && validated.processedOrders > 0) {
    validated.totalOrders = validated.processedOrders;
  }

  // Validate growth percentages (should be reasonable)
  if (Math.abs(validated.monthlyGrowth) > 10000) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('dataValidation: Suspicious monthly growth value:', validated.monthlyGrowth);
    }
    validated.monthlyGrowth = 0;
  }

  if (Math.abs(validated.commissionGrowth) > 10000) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('dataValidation: Suspicious commission growth value:', validated.commissionGrowth);
    }
    validated.commissionGrowth = 0;
  }

  // Validate commission rate (should be between 0 and 1)
  if (validated.averageCommissionRate > 1) {
    validated.averageCommissionRate = validated.averageCommissionRate / 100;
  }
  if (validated.averageCommissionRate < 0 || validated.averageCommissionRate > 1) {
    validated.averageCommissionRate = 0;
  }

  return validated;
};

/**
 * Validates brands data from API response
 * @param {Array|Object} data - Raw brands data from API
 * @returns {Array} Validated brands array
 */
export const validateBrandsData = (data) => {
  let brands = [];

  // Handle different response formats
  if (Array.isArray(data)) {
    brands = data;
  } else if (data && Array.isArray(data.brands)) {
    brands = data.brands;
  } else if (data && typeof data === 'object') {
    brands = [data]; // Single brand response
  }

  // Validate each brand object
  return brands.filter(brand => {
    if (!brand || typeof brand !== 'object') return false;
    
    // Must have required fields
    if (!brand.id) return false;
    
    return true;
  }).map(brand => ({
    ...brand,
    isActive: Boolean(brand.isActive),
    name: String(brand.name || 'Unnamed Brand'),
    defaultCommissionRate: validateCommissionRate(brand.defaultCommissionRate),
    lastSyncError: brand.lastSyncError || null
  }));
};

/**
 * Validates commission rate value
 * @param {any} rate - Commission rate to validate
 * @returns {number} Valid commission rate (0-1)
 */
export const validateCommissionRate = (rate) => {
  const numRate = parseFloat(rate);
  if (isNaN(numRate) || !isFinite(numRate)) return 0;
  
  // Convert percentage to decimal if needed
  if (numRate > 1 && numRate <= 100) {
    return numRate / 100;
  }
  
  // Ensure within valid range
  return Math.max(0, Math.min(1, numRate));
};

/**
 * Gets default analytics data structure
 * @returns {Object} Default analytics data
 */
export const getAnalyticsDefaults = () => ({
  totalRevenue: 0,
  totalCommission: 0,
  totalOrders: 0,
  processedOrders: 0,
  monthlyGrowth: 0,
  commissionGrowth: 0,
  averageOrderValue: 0,
  averageCommissionRate: 0,
  totalShippingFees: 0,
  topBrand: '',
  topMarketplace: ''
});

/**
 * Creates safe data accessors with validation
 * @param {Object} data - Raw data
 * @param {Function} validator - Validation function
 * @returns {Object} Safe data accessor
 */
export const createSafeDataAccessor = (data, validator) => {
  let validatedData = null;
  let validationError = null;

  try {
    validatedData = validator(data);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('dataValidation: Data validation error:', error);
    }
    validationError = error;
    validatedData = validator(null); // Get defaults
  }

  return {
    data: validatedData,
    isValid: !validationError,
    error: validationError,
    hasData: data != null
  };
};

/**
 * Validates API parameter inputs
 * @param {Object} params - API parameters
 * @returns {Object} Validated parameters
 */
export const validateApiParams = (params = {}) => {
  const validated = {};

  // Validate brand parameter
  if (params.brand) {
    if (params.brand === 'all' || typeof params.brand === 'string') {
      validated.brand = params.brand;
    }
  } else {
    validated.brand = 'all';
  }

  // Validate days parameter
  const days = parseInt(params.days);
  if (!isNaN(days) && days > 0 && days <= 365) {
    validated.days = days;
  } else {
    validated.days = 30;
  }

  // Validate date ranges if provided
  if (params.startDate) {
    const startDate = new Date(params.startDate);
    if (!isNaN(startDate.getTime())) {
      validated.startDate = params.startDate;
    }
  }

  if (params.endDate) {
    const endDate = new Date(params.endDate);
    if (!isNaN(endDate.getTime())) {
      validated.endDate = params.endDate;
    }
  }

  return validated;
};